export interface DayContent {
  topic: string;
  task: string;
}

export interface WeekData {
  weekNum: number;
  title: string;
  phase: 1 | 2 | 3 | 4 | 5 | 6;
  phaseLabel: string;
  days: [DayContent, DayContent, DayContent, DayContent, DayContent];
  assessment: string;
  keyConceptsForFlashcard: string[];
}

export const PHASE_META: Record<number, { label: string; color: string; textColor: string; border: string; weeks: [number, number] }> = {
  1: { label: 'Foundations', color: 'var(--phase1-bg)', textColor: 'var(--phase1-text)', border: 'var(--phase1-border)', weeks: [1, 20] },
  2: { label: 'Frontend', color: 'var(--phase2-bg)', textColor: 'var(--phase2-text)', border: 'var(--phase2-border)', weeks: [21, 36] },
  3: { label: 'Backend', color: 'var(--phase3-bg)', textColor: 'var(--phase3-text)', border: 'var(--phase3-border)', weeks: [37, 52] },
  4: { label: 'AI Integration', color: 'var(--phase4-bg)', textColor: 'var(--phase4-text)', border: 'var(--phase4-border)', weeks: [53, 64] },
  5: { label: 'DevOps', color: 'var(--phase5-bg)', textColor: 'var(--phase5-text)', border: 'var(--phase5-border)', weeks: [65, 72] },
  6: { label: 'Capstone', color: 'var(--phase6-bg)', textColor: 'var(--phase6-text)', border: 'var(--phase6-border)', weeks: [73, 80] },
};

export const CURRICULUM: WeekData[] = [
  // ── PHASE 1: FOUNDATIONS ──────────────────────────────────────────────────
  {
    weekNum: 1, title: 'Developer Environment & Git', phase: 1, phaseLabel: 'Foundations',
    days: [
      { topic: 'Terminal & shell basics', task: 'Configure zsh with Oh My Zsh and custom aliases for dev workflow' },
      { topic: 'Git fundamentals', task: 'Init a repo, make 10 commits, practice diff and log' },
      { topic: 'Branching & merging', task: 'Create feature branch, simulate merge conflict, resolve it' },
      { topic: 'Remote repos & GitHub', task: 'Push repo to GitHub, open a PR, review diff in browser' },
      { topic: 'VS Code & extensions', task: 'Install ESLint, Prettier, GitLens; configure workspace settings.json' },
    ],
    assessment: 'Create a git workflow cheatsheet repo with README, .gitignore, and branch strategy documented',
    keyConceptsForFlashcard: ['git rebase vs merge', 'HEAD detached state', 'git stash', 'conventional commits'],
  },
  {
    weekNum: 2, title: 'HTML5 Fundamentals', phase: 1, phaseLabel: 'Foundations',
    days: [
      { topic: 'Semantic HTML structure', task: 'Build a blog post page using only semantic elements' },
      { topic: 'Forms & input types', task: 'Create a multi-field registration form with all HTML5 input types' },
      { topic: 'Media elements', task: 'Build a media page with video, audio, figure, and picture elements' },
      { topic: 'Accessibility basics', task: 'Audit a page with axe DevTools, fix all critical ARIA issues' },
      { topic: 'Meta tags & SEO', task: 'Add Open Graph, Twitter Card, and structured data to a page' },
    ],
    assessment: 'Build a fully semantic personal portfolio HTML skeleton with proper landmark roles',
    keyConceptsForFlashcard: ['landmark roles', 'aria-label vs aria-labelledby', 'srcset', 'details/summary'],
  },
  {
    weekNum: 3, title: 'CSS Core & Layout', phase: 1, phaseLabel: 'Foundations',
    days: [
      { topic: 'Box model & specificity', task: 'Debug 5 CSS specificity conflicts in a provided stylesheet' },
      { topic: 'Flexbox mastery', task: 'Recreate 6 common UI layouts using only flexbox' },
      { topic: 'CSS Grid', task: 'Build a magazine-style grid layout with named template areas' },
      { topic: 'Responsive design', task: 'Make a dashboard layout responsive from 320px to 1440px' },
      { topic: 'Custom properties & themes', task: 'Implement a dark/light theme toggle using CSS variables' },
    ],
    assessment: 'Build a responsive landing page using grid + flexbox with dark/light theme toggle',
    keyConceptsForFlashcard: ['stacking context', 'BFC', 'grid auto-placement', 'clamp()'],
  },
  {
    weekNum: 4, title: 'JavaScript Core', phase: 1, phaseLabel: 'Foundations',
    days: [
      { topic: 'Data types & coercion', task: 'Write 20 type-coercion quiz functions with explanations' },
      { topic: 'Functions & scope', task: 'Implement a module pattern using IIFE and closures' },
      { topic: 'Arrays & higher-order functions', task: 'Solve 10 array manipulation challenges without loops' },
      { topic: 'Objects & prototypes', task: 'Build a linked list using prototype chain inheritance' },
      { topic: 'DOM manipulation', task: 'Build a live-search component with no frameworks' },
    ],
    assessment: 'Build a vanilla JS todo app with localStorage persistence and filter tabs',
    keyConceptsForFlashcard: ['event loop', 'prototype chain', 'closure', 'hoisting'],
  },
  {
    weekNum: 5, title: 'Async JS & Closures', phase: 1, phaseLabel: 'Foundations',
    days: [
      { topic: 'Callbacks & callback hell', task: 'Refactor nested callback code to use named functions' },
      { topic: 'Promises', task: 'Implement Promise.all, Promise.race, and Promise.allSettled from scratch' },
      { topic: 'Async/await', task: 'Convert a 5-step promise chain to async/await with error handling' },
      { topic: 'Fetch API & error handling', task: 'Build a weather widget with retry logic and timeout' },
      { topic: 'Closures in depth', task: 'Implement a memoize function and an event emitter using closures' },
    ],
    assessment: 'Build an async data fetcher with retry, timeout, caching, and abort controller',
    keyConceptsForFlashcard: ['microtask queue', 'promise chaining', 'AbortController', 'async iterator'],
  },
  {
    weekNum: 6, title: 'TypeScript Fundamentals', phase: 1, phaseLabel: 'Foundations',
    days: [
      { topic: 'Types, interfaces & unions', task: 'Type a 200-line JS file with zero any usage' },
      { topic: 'Generics', task: 'Write 5 generic utility functions (pick, omit, partial, required, readonly)' },
      { topic: 'Type narrowing & guards', task: 'Implement a type-safe event system with discriminated unions' },
      { topic: 'Utility types', task: 'Solve 15 TypeScript challenges from type-challenges repo' },
      { topic: 'TS config & strict mode', task: 'Migrate a JS project to strict TypeScript with no errors' },
    ],
    assessment: 'Type a full REST API client with generics, proper error types, and zero any',
    keyConceptsForFlashcard: ['conditional types', 'infer keyword', 'mapped types', 'template literal types'],
  },
  {
    weekNum: 7, title: 'Data Structures I', phase: 1, phaseLabel: 'Foundations',
    days: [
      { topic: 'Arrays & strings', task: 'Solve 5 LeetCode easy array problems with time/space analysis' },
      { topic: 'Linked lists', task: 'Implement singly and doubly linked lists with all operations' },
      { topic: 'Stacks & queues', task: 'Use stack to implement valid parentheses and queue for BFS' },
      { topic: 'Hash maps', task: 'Solve two-sum, group anagrams, and LRU cache using hash maps' },
      { topic: 'Binary search', task: 'Implement binary search variants (first/last occurrence, rotated array)' },
    ],
    assessment: 'Solve 5 medium LeetCode problems covering arrays, linked lists, and hash maps with O(n) solutions',
    keyConceptsForFlashcard: ['amortized complexity', 'hash collision', 'sentinel node', 'sliding window'],
  },
  {
    weekNum: 8, title: 'Data Structures II', phase: 1, phaseLabel: 'Foundations',
    days: [
      { topic: 'Trees & BST', task: 'Implement BST with insert, delete, search, and all three traversals' },
      { topic: 'Heaps & priority queues', task: 'Build a min-heap from scratch and use it for K largest elements' },
      { topic: 'Graphs basics', task: 'Implement adjacency list graph with BFS and DFS' },
      { topic: 'Dynamic programming I', task: 'Solve coin change, climbing stairs, and knapsack with memoization' },
      { topic: 'Sorting algorithms', task: 'Implement mergesort and quicksort, benchmark against built-in sort' },
    ],
    assessment: 'Solve 4 medium tree/graph problems demonstrating DFS, BFS, and DP approaches',
    keyConceptsForFlashcard: ['heap property', 'topological sort', 'cycle detection', 'DP recurrence'],
  },
  {
    weekNum: 9, title: 'Node.js Core', phase: 1, phaseLabel: 'Foundations',
    days: [
      { topic: 'Node.js runtime & event loop', task: 'Trace execution order of 10 async snippets in Node' },
      { topic: 'Modules: CJS vs ESM', task: 'Migrate a CJS project to ESM with proper exports' },
      { topic: 'File system & streams', task: 'Build a CSV parser using readable streams with backpressure' },
      { topic: 'HTTP module & servers', task: 'Build a file server from scratch using http module only' },
      { topic: 'Worker threads & child processes', task: 'Offload a CPU-intensive task to a worker thread' },
    ],
    assessment: 'Build a Node.js CLI tool that reads a directory, generates a file tree, and outputs JSON',
    keyConceptsForFlashcard: ['libuv', 'stream backpressure', 'cluster module', 'process.nextTick vs setImmediate'],
  },
  {
    weekNum: 10, title: 'SQL & Relational Databases', phase: 1, phaseLabel: 'Foundations',
    days: [
      { topic: 'Relational model & normalization', task: 'Design a 3NF schema for an e-commerce database' },
      { topic: 'SQL queries & joins', task: 'Write 20 progressively complex queries on a sample dataset' },
      { topic: 'Indexes & query planning', task: 'Use EXPLAIN ANALYZE to optimize 5 slow queries' },
      { topic: 'Transactions & ACID', task: 'Implement a bank transfer with rollback on failure' },
      { topic: 'PostgreSQL features', task: 'Use CTEs, window functions, and JSON columns in 3 queries' },
    ],
    assessment: 'Design and query a multi-table schema for a social network with complex aggregations',
    keyConceptsForFlashcard: ['MVCC', 'B-tree index', 'N+1 query', 'isolation levels'],
  },
  {
    weekNum: 11, title: 'NoSQL & MongoDB', phase: 1, phaseLabel: 'Foundations',
    days: [
      { topic: 'Document model & schema design', task: 'Model a blog system — embed vs reference tradeoffs' },
      { topic: 'CRUD & aggregation pipeline', task: 'Write 10 aggregation queries with $group, $lookup, $unwind' },
      { topic: 'Indexes in MongoDB', task: 'Add compound and text indexes, profile queries with explain()' },
      { topic: 'Redis fundamentals', task: 'Implement session storage and rate limiting using Redis' },
      { topic: 'Choosing SQL vs NoSQL', task: 'Write a decision matrix for 5 different application types' },
    ],
    assessment: 'Build a MongoDB aggregation pipeline that generates a monthly sales report from raw order data',
    keyConceptsForFlashcard: ['eventual consistency', 'CAP theorem', 'Redis data types', 'aggregation stages'],
  },
  {
    weekNum: 12, title: 'REST API Design', phase: 1, phaseLabel: 'Foundations',
    days: [
      { topic: 'REST principles & HTTP verbs', task: 'Audit 10 public APIs for REST constraint violations' },
      { topic: 'Express.js & routing', task: 'Build a RESTful CRUD API for a resource with 5 endpoints' },
      { topic: 'Middleware & error handling', task: 'Add logging, rate limiting, and global error middleware' },
      { topic: 'Request validation', task: 'Add Zod validation to all endpoints with proper 400 responses' },
      { topic: 'API documentation with OpenAPI', task: 'Write a complete OpenAPI 3.0 spec for your API' },
    ],
    assessment: 'Build a fully documented REST API with auth middleware, validation, and OpenAPI spec',
    keyConceptsForFlashcard: ['HATEOAS', 'idempotency', 'content negotiation', 'ETag'],
  },
  {
    weekNum: 13, title: 'Testing I — Unit & Integration', phase: 1, phaseLabel: 'Foundations',
    days: [
      { topic: 'Testing philosophy & TDD', task: 'Rewrite a module TDD-style: tests first, then implementation' },
      { topic: 'Jest & Vitest fundamentals', task: 'Write 20 unit tests covering pure functions with edge cases' },
      { topic: 'Mocking & spies', task: 'Mock a database module and test service layer in isolation' },
      { topic: 'Integration testing', task: 'Test a REST API endpoint with supertest including DB calls' },
      { topic: 'Code coverage', task: 'Achieve 90% line coverage on a 300-line module' },
    ],
    assessment: 'Write a full test suite for a user authentication module with 85%+ coverage',
    keyConceptsForFlashcard: ['test doubles', 'AAA pattern', 'test pyramid', 'mutation testing'],
  },
  {
    weekNum: 14, title: 'Testing II — E2E & Quality', phase: 1, phaseLabel: 'Foundations',
    days: [
      { topic: 'E2E testing with Playwright', task: 'Write 3 critical user journey tests (signup, purchase, search)' },
      { topic: 'Component testing', task: 'Test 5 UI components with Testing Library and user-event' },
      { topic: 'Visual regression testing', task: 'Add Percy or Chromatic snapshot tests to a component library' },
      { topic: 'Performance testing', task: 'Load test an API with k6, find and fix the bottleneck' },
      { topic: 'Test CI integration', task: 'Configure GitHub Actions to run all test suites on PR' },
    ],
    assessment: 'Build a CI pipeline that runs unit, integration, and E2E tests with coverage gates',
    keyConceptsForFlashcard: ['flaky tests', 'test isolation', 'page object model', 'contract testing'],
  },
  {
    weekNum: 15, title: 'System Design I', phase: 1, phaseLabel: 'Foundations',
    days: [
      { topic: 'Scalability fundamentals', task: 'Design a URL shortener — capacity estimation and data model' },
      { topic: 'Load balancing & caching', task: 'Design Twitter timeline with CDN and cache invalidation strategy' },
      { topic: 'Database sharding & replication', task: 'Design a chat system storage layer for 1B messages/day' },
      { topic: 'CAP theorem & consistency', task: 'Compare eventual vs strong consistency for 3 real use cases' },
      { topic: 'Rate limiting & circuit breaker', task: 'Design a rate limiter supporting 10K RPS per user' },
    ],
    assessment: 'Design Instagram: storage, CDN, feed generation, and scaling to 100M daily users',
    keyConceptsForFlashcard: ['consistent hashing', 'write-ahead log', 'read replica', 'thundering herd'],
  },
  {
    weekNum: 16, title: 'System Design II', phase: 1, phaseLabel: 'Foundations',
    days: [
      { topic: 'Event-driven architecture', task: 'Design a notification system using pub/sub with Kafka topics' },
      { topic: 'Distributed transactions', task: 'Implement saga pattern for a multi-service order flow' },
      { topic: 'API gateway patterns', task: 'Design an API gateway with auth, rate limiting, and routing' },
      { topic: 'Search system design', task: 'Design a type-ahead search for 500M products' },
      { topic: 'Distributed caching', task: 'Design a cache layer for a global e-commerce platform' },
    ],
    assessment: 'Design Google Drive: file storage, sync, sharing, and conflict resolution at scale',
    keyConceptsForFlashcard: ['two-phase commit', 'outbox pattern', 'CQRS', 'vector clock'],
  },
  {
    weekNum: 17, title: 'Developer Tooling', phase: 1, phaseLabel: 'Foundations',
    days: [
      { topic: 'Bundlers: Webpack & Vite', task: 'Configure Vite with code splitting, tree shaking, and env vars' },
      { topic: 'Linting & formatting', task: 'Set up ESLint with TypeScript plugin and strict Prettier rules' },
      { topic: 'Monorepo with Turborepo', task: 'Create a monorepo with shared UI and utils packages' },
      { topic: 'npm scripts & Makefiles', task: 'Write a Makefile for dev, test, build, and deploy targets' },
      { topic: 'Debugging techniques', task: 'Debug 5 tricky async bugs using Node inspector and VS Code' },
    ],
    assessment: 'Set up a production-grade monorepo with CI, shared packages, and automated releases',
    keyConceptsForFlashcard: ['tree shaking', 'code splitting', 'source maps', 'HMR'],
  },
  {
    weekNum: 18, title: 'Web Security', phase: 1, phaseLabel: 'Foundations',
    days: [
      { topic: 'OWASP Top 10', task: 'Exploit and fix XSS, CSRF, and SQL injection in a demo app' },
      { topic: 'Authentication security', task: 'Implement secure password hashing with Argon2 and MFA' },
      { topic: 'HTTPS & TLS', task: 'Configure HSTS, certificate pinning, and CSP headers' },
      { topic: 'JWT security', task: 'Audit JWT implementation for alg:none, weak secrets, and expiry' },
      { topic: 'Dependency security', task: 'Run npm audit, fix 10 vulnerabilities, set up Snyk CI scan' },
    ],
    assessment: 'Security audit a provided Express app — find 8 vulnerabilities and write fix PRs',
    keyConceptsForFlashcard: ['CORS', 'SameSite cookie', 'Content-Security-Policy', 'SSRF'],
  },
  {
    weekNum: 19, title: 'Performance Engineering', phase: 1, phaseLabel: 'Foundations',
    days: [
      { topic: 'Browser rendering pipeline', task: 'Eliminate render-blocking resources on a test page' },
      { topic: 'JavaScript performance', task: 'Profile a slow React render, fix with memoization and virtualization' },
      { topic: 'Network performance', task: 'Implement HTTP/2 push, preload, and prefetch on a site' },
      { topic: 'Image optimization', task: 'Automate WebP conversion, lazy loading, and blur placeholder' },
      { topic: 'Core Web Vitals', task: 'Score 90+ on all CWV metrics for a provided slow page' },
    ],
    assessment: 'Take a 40-score Lighthouse page to 90+ across all categories with documented changes',
    keyConceptsForFlashcard: ['LCP', 'CLS', 'INP', 'critical rendering path'],
  },
  {
    weekNum: 20, title: 'Phase 1 Capstone', phase: 1, phaseLabel: 'Foundations',
    days: [
      { topic: 'Capstone planning', task: 'Write architecture doc for a full-stack app using Phase 1 skills' },
      { topic: 'Backend implementation', task: 'Build REST API with Node, PostgreSQL, auth, and tests' },
      { topic: 'Frontend implementation', task: 'Build vanilla JS frontend consuming the API' },
      { topic: 'Testing & security audit', task: 'Write test suite and fix all security issues found' },
      { topic: 'Documentation & deployment', task: 'Deploy to Railway, write README with architecture diagram' },
    ],
    assessment: 'Present Phase 1 capstone: code review, architecture walkthrough, and Q&A',
    keyConceptsForFlashcard: ['project structure', 'deployment checklist', 'API versioning', 'README standards'],
  },

  // ── PHASE 2: FRONTEND ────────────────────────────────────────────────────
  {
    weekNum: 21, title: 'React Fundamentals', phase: 2, phaseLabel: 'Frontend',
    days: [
      { topic: 'JSX & component model', task: 'Convert 5 HTML templates into composable React components' },
      { topic: 'Props & state', task: 'Build a controlled form with 8 field types and validation' },
      { topic: 'useEffect & lifecycle', task: 'Implement data fetching with loading/error/success states' },
      { topic: 'Event handling', task: 'Build a drag-and-drop list using only React event handlers' },
      { topic: 'Lists & keys', task: 'Build a virtualized list rendering 10,000 items at 60fps' },
    ],
    assessment: 'Build a multi-step form wizard with validation, progress, and localStorage persistence',
    keyConceptsForFlashcard: ['reconciliation', 'fiber', 'synthetic events', 'controlled vs uncontrolled'],
  },
  {
    weekNum: 22, title: 'Advanced React Patterns', phase: 2, phaseLabel: 'Frontend',
    days: [
      { topic: 'Custom hooks', task: 'Write useLocalStorage, useDebounce, useFetch, useMediaQuery hooks' },
      { topic: 'Context API', task: 'Implement a theme + auth context without prop drilling' },
      { topic: 'Compound components', task: 'Build a Tabs component using compound component pattern' },
      { topic: 'Render props & HOCs', task: 'Wrap a data table with sorting/filtering HOC' },
      { topic: 'useReducer & state machines', task: 'Model a checkout flow as an explicit state machine' },
    ],
    assessment: 'Build a data table library with sorting, filtering, pagination using compound components',
    keyConceptsForFlashcard: ['render props', 'HOC pitfalls', 'compound component', 'state machine'],
  },
  {
    weekNum: 23, title: 'State Management', phase: 2, phaseLabel: 'Frontend',
    days: [
      { topic: 'Zustand fundamentals', task: 'Migrate a Context-based cart to Zustand with devtools' },
      { topic: 'Jotai & atomic state', task: 'Model a spreadsheet-like UI with atomic state slices' },
      { topic: 'TanStack Query', task: 'Replace useEffect data fetching with TanStack Query mutations' },
      { topic: 'Optimistic updates', task: 'Implement optimistic UI for a like button with rollback' },
      { topic: 'State colocation', task: 'Audit an app and move state to the correct component level' },
    ],
    assessment: 'Build a real-time collaborative note editor using Zustand with undo/redo history',
    keyConceptsForFlashcard: ['selector memoization', 'stale-while-revalidate', 'optimistic update', 'atom'],
  },
  {
    weekNum: 24, title: 'React Performance', phase: 2, phaseLabel: 'Frontend',
    days: [
      { topic: 'React DevTools Profiler', task: 'Profile a slow component tree, identify top 3 bottlenecks' },
      { topic: 'memo, useMemo, useCallback', task: 'Optimize a dashboard with 50 child components' },
      { topic: 'Code splitting & lazy', task: 'Split a large app into 8 lazy-loaded route chunks' },
      { topic: 'Virtualization', task: 'Implement react-virtual for a 100K row data grid' },
      { topic: 'Concurrent features', task: 'Use useTransition and useDeferredValue for search filtering' },
    ],
    assessment: 'Optimize a provided slow React app from 2s to <200ms interaction latency',
    keyConceptsForFlashcard: ['bailout optimization', 'tearing', 'startTransition', 'time slicing'],
  },
  {
    weekNum: 25, title: 'Component Design Systems', phase: 2, phaseLabel: 'Frontend',
    days: [
      { topic: 'Design tokens', task: 'Define a token system for color, spacing, typography, and shadow' },
      { topic: 'Primitive components', task: 'Build Button, Input, Badge, Card with full variant API' },
      { topic: 'Compound components', task: 'Build Dialog, Dropdown, Popover with focus management' },
      { topic: 'Storybook', task: 'Document all components with 3 stories each and accessibility addon' },
      { topic: 'Figma to code', task: 'Implement a provided Figma design pixel-perfectly in React' },
    ],
    assessment: 'Publish a 12-component design system to npm with Storybook docs and TypeScript types',
    keyConceptsForFlashcard: ['design tokens', 'polymorphic components', 'Storybook args', 'visual regression'],
  },
  {
    weekNum: 26, title: 'Next.js Fundamentals', phase: 2, phaseLabel: 'Frontend',
    days: [
      { topic: 'Pages Router & routing', task: 'Build a multi-page app with dynamic routes and catch-all' },
      { topic: 'Data fetching: SSG & SSR', task: 'Implement getStaticProps and getServerSideProps for a blog' },
      { topic: 'API routes', task: 'Build 5 API routes with proper HTTP methods and error handling' },
      { topic: 'Image & font optimization', task: 'Optimize all images with next/image and load custom fonts' },
      { topic: 'Deployment to Vercel', task: 'Deploy with preview environments, env vars, and custom domain' },
    ],
    assessment: 'Build a Next.js blog with MDX, ISR, sitemap, and 90+ Lighthouse score',
    keyConceptsForFlashcard: ['ISR', 'getStaticPaths fallback', 'edge runtime', 'next/link prefetch'],
  },
  {
    weekNum: 27, title: 'Next.js App Router & RSC', phase: 2, phaseLabel: 'Frontend',
    days: [
      { topic: 'App Router fundamentals', task: 'Migrate a Pages Router app to App Router with layouts' },
      { topic: 'React Server Components', task: 'Identify and convert 5 components to Server Components' },
      { topic: 'Streaming & Suspense', task: 'Add streaming to a slow data page with Suspense boundaries' },
      { topic: 'Server Actions', task: 'Replace 3 API routes with Server Actions in a form' },
      { topic: 'Caching strategies', task: 'Implement revalidateTag and revalidatePath in a CMS app' },
    ],
    assessment: 'Build a Next.js 14 dashboard with RSC, streaming, Server Actions, and optimistic UI',
    keyConceptsForFlashcard: ['RSC payload', 'server action closure', 'route segment config', 'partial prerendering'],
  },
  {
    weekNum: 28, title: 'CSS-in-JS & Tailwind', phase: 2, phaseLabel: 'Frontend',
    days: [
      { topic: 'Tailwind CSS mastery', task: 'Build a full admin dashboard layout in Tailwind only' },
      { topic: 'Tailwind plugins & config', task: 'Write a Tailwind plugin for custom design tokens' },
      { topic: 'CSS Modules', task: 'Migrate a styled-components app to CSS Modules' },
      { topic: 'Vanilla Extract', task: 'Build a type-safe theme system with Vanilla Extract' },
      { topic: 'Responsive & adaptive', task: 'Implement container queries for a card component library' },
    ],
    assessment: 'Build a pixel-perfect marketing page from a Figma file using Tailwind with dark mode',
    keyConceptsForFlashcard: ['JIT mode', 'container queries', 'CSS layers', 'style encapsulation'],
  },
  {
    weekNum: 29, title: 'Animations & Motion', phase: 2, phaseLabel: 'Frontend',
    days: [
      { topic: 'CSS animations & transitions', task: 'Animate 8 UI interactions using only CSS' },
      { topic: 'Framer Motion fundamentals', task: 'Add page transitions and shared layout animations' },
      { topic: 'GSAP ScrollTrigger', task: 'Build a scroll-driven storytelling page with pinned sections' },
      { topic: 'Lottie & SVG animations', task: 'Integrate 3 Lottie animations with playback control' },
      { topic: 'Animation performance', task: 'Audit animations for layout thrash, use will-change correctly' },
    ],
    assessment: 'Build an animated product landing page with scroll effects and micro-interactions',
    keyConceptsForFlashcard: ['FLIP technique', 'compositor layers', 'spring physics', 'requestAnimationFrame'],
  },
  {
    weekNum: 30, title: 'Core Web Vitals', phase: 2, phaseLabel: 'Frontend',
    days: [
      { topic: 'LCP optimization', task: 'Identify LCP element, preload hero image, achieve <2.5s LCP' },
      { topic: 'CLS prevention', task: 'Fix 5 CLS issues: fonts, images, dynamic content' },
      { topic: 'INP optimization', task: 'Reduce long tasks blocking interaction to <200ms' },
      { topic: 'Real user monitoring', task: 'Set up web-vitals library and send to analytics endpoint' },
      { topic: 'Field vs lab data', task: 'Analyze CrUX data for a real site and create fix roadmap' },
    ],
    assessment: 'Achieve green CWV on all three metrics for a provided underperforming Next.js app',
    keyConceptsForFlashcard: ['TTFB', 'FID vs INP', 'CrUX', 'observer pattern'],
  },
  {
    weekNum: 31, title: 'Web Accessibility', phase: 2, phaseLabel: 'Frontend',
    days: [
      { topic: 'WCAG 2.1 AA compliance', task: 'Audit 10 components against all WCAG 2.1 AA criteria' },
      { topic: 'Keyboard navigation', task: 'Implement full keyboard navigation for a modal and dropdown' },
      { topic: 'Screen reader testing', task: 'Test app with VoiceOver and NVDA, fix 8 issues found' },
      { topic: 'Focus management', task: 'Implement focus trapping in modals and focus restoration on close' },
      { topic: 'Color & motion', task: 'Add prefers-reduced-motion and ensure 4.5:1 contrast ratio' },
    ],
    assessment: 'Make a provided inaccessible app WCAG 2.1 AA compliant with documented changes',
    keyConceptsForFlashcard: ['live regions', 'focus trap', 'skip links', 'accessible name'],
  },
  {
    weekNum: 32, title: 'Testing React', phase: 2, phaseLabel: 'Frontend',
    days: [
      { topic: 'React Testing Library', task: 'Test 5 components using getByRole and user-event' },
      { topic: 'Testing async components', task: 'Test data fetching components with MSW mock service worker' },
      { topic: 'Testing hooks', task: 'Test custom hooks using renderHook with act()' },
      { topic: 'Snapshot & visual testing', task: 'Add Storybook interaction tests and visual snapshots' },
      { topic: 'Testing Next.js', task: 'Test App Router pages with server and client component mocks' },
    ],
    assessment: 'Write 40+ tests for a React dashboard with 80%+ coverage and CI integration',
    keyConceptsForFlashcard: ['MSW', 'user-event vs fireEvent', 'testing-library queries', 'accessibility queries'],
  },
  {
    weekNum: 33, title: 'GraphQL Client', phase: 2, phaseLabel: 'Frontend',
    days: [
      { topic: 'GraphQL fundamentals', task: 'Write 10 queries and mutations against a public GraphQL API' },
      { topic: 'Apollo Client', task: 'Set up Apollo with cache, optimistic updates, and error handling' },
      { topic: 'Code generation', task: 'Generate TypeScript types from GraphQL schema with graphql-codegen' },
      { topic: 'Subscriptions', task: 'Add real-time updates to a dashboard using GraphQL subscriptions' },
      { topic: 'Fragments & normalization', task: 'Optimize cache with fragments and normalized store' },
    ],
    assessment: 'Build a GitHub dashboard using GitHub GraphQL API with pagination and subscriptions',
    keyConceptsForFlashcard: ['normalized cache', 'fragment masking', 'persisted queries', 'DataLoader'],
  },
  {
    weekNum: 34, title: 'WebSockets & Real-time', phase: 2, phaseLabel: 'Frontend',
    days: [
      { topic: 'WebSocket protocol', task: 'Build a raw WebSocket chat without any library' },
      { topic: 'Socket.IO', task: 'Add rooms, namespaces, and reconnection to a chat app' },
      { topic: 'SSE & long polling', task: 'Implement a live notification feed using Server-Sent Events' },
      { topic: 'Presence & collaboration', task: 'Build live user presence indicators (typing, online, cursor)' },
      { topic: 'State sync strategies', task: 'Implement operational transforms for collaborative text editing' },
    ],
    assessment: 'Build a real-time collaborative whiteboard with cursors, shapes, and undo/redo',
    keyConceptsForFlashcard: ['WebSocket handshake', 'CRDT', 'operational transform', 'SSE vs WebSocket'],
  },
  {
    weekNum: 35, title: 'Progressive Web Apps', phase: 2, phaseLabel: 'Frontend',
    days: [
      { topic: 'Service workers', task: 'Implement offline-first caching strategy for a news app' },
      { topic: 'Web app manifest', task: 'Make an app installable with splash screen and shortcuts' },
      { topic: 'Background sync', task: 'Queue form submissions when offline and sync on reconnect' },
      { topic: 'Push notifications', task: 'Implement web push with subscription management' },
      { topic: 'PWA audit & optimization', task: 'Score 100 on Lighthouse PWA audit' },
    ],
    assessment: 'Build an offline-capable PWA that works without internet and passes all Lighthouse checks',
    keyConceptsForFlashcard: ['cache strategies', 'workbox', 'push protocol', 'install prompt'],
  },
  {
    weekNum: 36, title: 'Phase 2 Capstone', phase: 2, phaseLabel: 'Frontend',
    days: [
      { topic: 'Capstone planning & design', task: 'Design a complex React/Next.js app architecture' },
      { topic: 'Core implementation', task: 'Build the main features with RSC, state management, and routing' },
      { topic: 'Polish & performance', task: 'Achieve 90+ Lighthouse, add animations and accessibility' },
      { topic: 'Testing', task: 'Write 50+ tests covering all critical user flows' },
      { topic: 'Deployment & documentation', task: 'Deploy to Vercel with preview URLs and component docs' },
    ],
    assessment: 'Present Phase 2 capstone: architecture decisions, performance metrics, and component library',
    keyConceptsForFlashcard: ['RSC vs client component', 'streaming SSR', 'app router caching', 'partial hydration'],
  },

  // ── PHASE 3: BACKEND ─────────────────────────────────────────────────────
  {
    weekNum: 37, title: 'Node.js API Architecture', phase: 3, phaseLabel: 'Backend',
    days: [
      { topic: 'Clean architecture in Node', task: 'Refactor a monolithic handler into layers (controller/service/repo)' },
      { topic: 'Dependency injection', task: 'Add a DI container (tsyringe) to a Node.js API' },
      { topic: 'Domain-driven design', task: 'Model a complex domain with aggregates and value objects' },
      { topic: 'Repository pattern', task: 'Abstract database access behind repository interfaces' },
      { topic: 'Error handling architecture', task: 'Implement typed error hierarchy and global error handler' },
    ],
    assessment: 'Refactor a provided messy API into clean architecture with DI, tests, and error types',
    keyConceptsForFlashcard: ['hexagonal architecture', 'aggregate root', 'ubiquitous language', 'DI container'],
  },
  {
    weekNum: 38, title: 'Authentication Systems', phase: 3, phaseLabel: 'Backend',
    days: [
      { topic: 'JWT access + refresh tokens', task: 'Implement token rotation with secure httpOnly cookies' },
      { topic: 'OAuth 2.0 & OIDC', task: 'Add Google and GitHub OAuth with PKCE flow' },
      { topic: 'Session-based auth', task: 'Implement server-side sessions with Redis and CSRF protection' },
      { topic: 'RBAC & permissions', task: 'Build role-based access control with resource-level permissions' },
      { topic: 'Passwordless & MFA', task: 'Add magic link login and TOTP-based two-factor authentication' },
    ],
    assessment: 'Build a complete auth system: JWT, OAuth, RBAC, MFA, and account recovery flow',
    keyConceptsForFlashcard: ['PKCE', 'token introspection', 'RBAC vs ABAC', 'session fixation'],
  },
  {
    weekNum: 39, title: 'ORM & Database Patterns', phase: 3, phaseLabel: 'Backend',
    days: [
      { topic: 'Prisma fundamentals', task: 'Model a complex schema with relations, enums, and migrations' },
      { topic: 'Query optimization with Prisma', task: 'Fix N+1 queries using select and include strategies' },
      { topic: 'Database migrations', task: 'Write 5 non-destructive migrations with rollback scripts' },
      { topic: 'Soft deletes & audit logs', task: 'Implement soft delete and full audit trail middleware' },
      { topic: 'Multi-tenancy patterns', task: 'Implement row-level tenant isolation in a SaaS schema' },
    ],
    assessment: 'Design and implement a multi-tenant SaaS schema with audit logging and migrations',
    keyConceptsForFlashcard: ['schema migration', 'optimistic locking', 'connection pooling', 'row-level security'],
  },
  {
    weekNum: 40, title: 'Caching Strategies', phase: 3, phaseLabel: 'Backend',
    days: [
      { topic: 'Redis caching patterns', task: 'Implement cache-aside, write-through, and write-behind' },
      { topic: 'Cache invalidation', task: 'Build a tag-based cache invalidation system' },
      { topic: 'Distributed caching', task: 'Set up Redis Cluster with consistent hashing' },
      { topic: 'HTTP caching', task: 'Implement ETags, Cache-Control, and CDN cache headers' },
      { topic: 'Database query caching', task: 'Add query result caching with automatic invalidation' },
    ],
    assessment: 'Reduce API p99 latency by 10x using a multi-layer caching strategy',
    keyConceptsForFlashcard: ['cache stampede', 'hot key problem', 'Bloom filter', 'cache warming'],
  },
  {
    weekNum: 41, title: 'Message Queues', phase: 3, phaseLabel: 'Backend',
    days: [
      { topic: 'BullMQ fundamentals', task: 'Process 1000 image resize jobs with concurrency and retries' },
      { topic: 'Kafka basics', task: 'Produce and consume events with consumer groups and offsets' },
      { topic: 'Dead letter queues', task: 'Implement DLQ with alerting for failed job analysis' },
      { topic: 'Event ordering & idempotency', task: 'Handle out-of-order events and duplicate delivery' },
      { topic: 'Queue monitoring', task: 'Set up BullMQ Board and Kafka UI for operational visibility' },
    ],
    assessment: 'Build an order processing pipeline with Kafka: place, validate, fulfill, notify',
    keyConceptsForFlashcard: ['consumer lag', 'exactly-once semantics', 'partition key', 'backpressure'],
  },
  {
    weekNum: 42, title: 'Microservices', phase: 3, phaseLabel: 'Backend',
    days: [
      { topic: 'Service decomposition', task: 'Split a monolith into 4 services with clear boundaries' },
      { topic: 'Inter-service communication', task: 'Implement sync (gRPC) and async (Kafka) communication' },
      { topic: 'Service discovery', task: 'Add Consul-based service discovery and health checks' },
      { topic: 'Saga pattern', task: 'Implement distributed transaction for an order saga' },
      { topic: 'Resilience patterns', task: 'Add circuit breaker, retry, and bulkhead with Resilience4j' },
    ],
    assessment: 'Build a 3-service e-commerce system with saga, circuit breaker, and distributed tracing',
    keyConceptsForFlashcard: ['strangler fig', 'choreography vs orchestration', 'bulkhead', 'service mesh'],
  },
  {
    weekNum: 43, title: 'GraphQL API', phase: 3, phaseLabel: 'Backend',
    days: [
      { topic: 'GraphQL server with Apollo', task: 'Build a schema-first API with resolvers and dataloaders' },
      { topic: 'N+1 and DataLoader', task: 'Eliminate N+1 queries using DataLoader batching' },
      { topic: 'Subscriptions', task: 'Implement real-time subscriptions with Redis pub/sub' },
      { topic: 'Schema federation', task: 'Split a schema into 3 federated subgraphs with Apollo Router' },
      { topic: 'Security & rate limiting', task: 'Add query depth limiting, cost analysis, and persisted queries' },
    ],
    assessment: 'Build a federated GraphQL API with DataLoader, subscriptions, and query cost analysis',
    keyConceptsForFlashcard: ['resolver chain', 'schema stitching', 'Apollo Federation', 'query complexity'],
  },
  {
    weekNum: 44, title: 'File Handling & Storage', phase: 3, phaseLabel: 'Backend',
    days: [
      { topic: 'S3 & object storage', task: 'Implement direct upload to S3 with presigned URLs' },
      { topic: 'Image processing', task: 'Build an image pipeline: resize, crop, watermark, CDN delivery' },
      { topic: 'Video transcoding', task: 'Queue FFmpeg jobs for multi-bitrate HLS streaming' },
      { topic: 'PDF generation', task: 'Generate invoices and reports as PDFs from HTML templates' },
      { topic: 'File security', task: 'Scan uploads for malware, validate MIME types, restrict file types' },
    ],
    assessment: 'Build a media service: upload, process, store, and serve images/videos with CDN',
    keyConceptsForFlashcard: ['presigned URL', 'multipart upload', 'HLS streaming', 'content-disposition'],
  },
  {
    weekNum: 45, title: 'Search & Elasticsearch', phase: 3, phaseLabel: 'Backend',
    days: [
      { topic: 'Elasticsearch fundamentals', task: 'Index 100K documents and run full-text search queries' },
      { topic: 'Mapping & analyzers', task: 'Configure custom analyzers for multilingual search' },
      { topic: 'Aggregations', task: 'Build a faceted search UI with price/category aggregations' },
      { topic: 'Search relevance tuning', task: 'Tune BM25 scoring and add boosting by recency' },
      { topic: 'Sync DB to Elasticsearch', task: 'Build a CDC pipeline syncing Postgres changes to ES' },
    ],
    assessment: 'Build a product search with full-text, filters, facets, and autocomplete',
    keyConceptsForFlashcard: ['inverted index', 'BM25', 'CDC', 'shards vs replicas'],
  },
  {
    weekNum: 46, title: 'Payments Integration', phase: 3, phaseLabel: 'Backend',
    days: [
      { topic: 'Stripe fundamentals', task: 'Implement one-time payment with webhooks and receipt email' },
      { topic: 'Subscriptions & billing', task: 'Build subscription with trial, upgrade, and proration' },
      { topic: 'Webhook security', task: 'Verify Stripe webhook signatures and handle idempotency' },
      { topic: 'Refunds & disputes', task: 'Handle refund flow and dispute webhook with Stripe' },
      { topic: 'International payments', task: 'Add currency conversion, tax calculation, and VAT' },
    ],
    assessment: 'Build a SaaS billing system: plans, subscriptions, invoices, and webhook handlers',
    keyConceptsForFlashcard: ['idempotency key', 'payment intent', 'webhook idempotency', 'SCA'],
  },
  {
    weekNum: 47, title: 'Email & Notifications', phase: 3, phaseLabel: 'Backend',
    days: [
      { topic: 'Transactional email with Resend', task: 'Build 5 email templates: welcome, reset, invoice, alert, digest' },
      { topic: 'Email deliverability', task: 'Configure SPF, DKIM, DMARC and test with mail-tester.com' },
      { topic: 'Push notifications', task: 'Implement FCM push for web and mobile with topic routing' },
      { topic: 'In-app notifications', task: 'Build real-time notification center with read/unread state' },
      { topic: 'Notification preferences', task: 'Build a preference center with channel and frequency controls' },
    ],
    assessment: 'Build a notification system: email, push, in-app with preferences and delivery tracking',
    keyConceptsForFlashcard: ['DKIM', 'bounce handling', 'notification fanout', 'digest batching'],
  },
  {
    weekNum: 48, title: 'Background Jobs', phase: 3, phaseLabel: 'Backend',
    days: [
      { topic: 'Cron jobs & scheduling', task: 'Schedule 5 recurring jobs with overlap prevention' },
      { topic: 'Long-running jobs', task: 'Build a report generation job with progress streaming' },
      { topic: 'Job queues with BullMQ', task: 'Implement priority queues and job dependencies' },
      { topic: 'Distributed locks', task: 'Prevent duplicate job execution with Redis distributed locks' },
      { topic: 'Job observability', task: 'Add structured logging, metrics, and alerting for jobs' },
    ],
    assessment: 'Build a data export pipeline: queue, process in chunks, notify user, upload to S3',
    keyConceptsForFlashcard: ['distributed lock', 'job starvation', 'graceful shutdown', 'exactly-once delivery'],
  },
  {
    weekNum: 49, title: 'API Gateway', phase: 3, phaseLabel: 'Backend',
    days: [
      { topic: 'Kong or AWS API Gateway', task: 'Route 5 microservices through a single API gateway' },
      { topic: 'Rate limiting & throttling', task: 'Implement token bucket rate limiting per user/IP/endpoint' },
      { topic: 'Request transformation', task: 'Add request/response transformation plugins to gateway' },
      { topic: 'API versioning strategies', task: 'Implement URL and header versioning with deprecation notices' },
      { topic: 'Gateway observability', task: 'Add distributed tracing through gateway with Jaeger' },
    ],
    assessment: 'Set up a production API gateway with auth, rate limiting, routing, and observability',
    keyConceptsForFlashcard: ['token bucket', 'API versioning', 'canary routing', 'service mesh vs gateway'],
  },
  {
    weekNum: 50, title: 'Monitoring & Observability', phase: 3, phaseLabel: 'Backend',
    days: [
      { topic: 'Structured logging', task: 'Add Pino logging with correlation IDs across all services' },
      { topic: 'Metrics with Prometheus', task: 'Instrument 10 custom business metrics with Prometheus' },
      { topic: 'Distributed tracing', task: 'Add OpenTelemetry tracing across 3 services' },
      { topic: 'Alerting & dashboards', task: 'Create Grafana dashboard with SLO alerts' },
      { topic: 'Error tracking', task: 'Set up Sentry with source maps and custom error context' },
    ],
    assessment: 'Build full observability stack: logs, metrics, traces, and dashboards for a microservice',
    keyConceptsForFlashcard: ['RED method', 'SLO vs SLA', 'cardinality', 'trace context propagation'],
  },
  {
    weekNum: 51, title: 'Database Scaling', phase: 3, phaseLabel: 'Backend',
    days: [
      { topic: 'Read replicas', task: 'Route read queries to replica, writes to primary with Prisma' },
      { topic: 'Connection pooling', task: 'Configure PgBouncer, tune pool size for 10K concurrent users' },
      { topic: 'Database partitioning', task: 'Partition a 1B row events table by date range' },
      { topic: 'Vertical vs horizontal scaling', task: 'Benchmark query performance at each scaling stage' },
      { topic: 'Backup & disaster recovery', task: 'Automate daily backups with point-in-time recovery test' },
    ],
    assessment: 'Scale a database from 1 to 100K concurrent users with documented architecture changes',
    keyConceptsForFlashcard: ['pgBouncer modes', 'table partitioning', 'PITR', 'logical replication'],
  },
  {
    weekNum: 52, title: 'Phase 3 Capstone', phase: 3, phaseLabel: 'Backend',
    days: [
      { topic: 'Capstone: API design', task: 'Design a production-grade API with all Phase 3 patterns' },
      { topic: 'Implementation sprint 1', task: 'Build auth, database layer, and core business logic' },
      { topic: 'Implementation sprint 2', task: 'Add caching, queues, file handling, and notifications' },
      { topic: 'Observability & testing', task: 'Add full observability stack and 70%+ test coverage' },
      { topic: 'Documentation & deploy', task: 'OpenAPI docs, Postman collection, deploy to Railway' },
    ],
    assessment: 'Present Phase 3 capstone: architecture review, load test results, and observability demo',
    keyConceptsForFlashcard: ['API design principles', 'horizontal scaling', 'zero-downtime deploy', 'runbook'],
  },

  // ── PHASE 4: AI INTEGRATION ───────────────────────────────────────────────
  {
    weekNum: 53, title: 'LLM Fundamentals & Prompt Engineering', phase: 4, phaseLabel: 'AI Integration',
    days: [
      { topic: 'How LLMs work', task: 'Write a 1000-word explainer on transformers, tokens, and temperature' },
      { topic: 'Prompt engineering techniques', task: 'Implement zero-shot, few-shot, chain-of-thought, and ReAct prompts' },
      { topic: 'System prompts & personas', task: 'Build 5 different AI personas with consistent behavior' },
      { topic: 'Prompt injection & safety', task: 'Red-team 10 prompts, document vulnerabilities and mitigations' },
      { topic: 'Evaluating LLM outputs', task: 'Build an eval harness testing prompt quality across 50 cases' },
    ],
    assessment: 'Build a prompt library with 20 production-ready prompts, each with eval test cases',
    keyConceptsForFlashcard: ['temperature vs top-p', 'context window', 'chain-of-thought', 'prompt injection'],
  },
  {
    weekNum: 54, title: 'OpenAI & Anthropic APIs', phase: 4, phaseLabel: 'AI Integration',
    days: [
      { topic: 'OpenAI API fundamentals', task: 'Build a streaming chat interface with conversation history' },
      { topic: 'Function calling & tools', task: 'Add 3 tool functions (weather, search, calculator) to a chat' },
      { topic: 'Anthropic Claude API', task: 'Compare Claude and GPT-4 on 10 benchmark tasks' },
      { topic: 'Structured outputs', task: 'Extract structured JSON from unstructured text reliably' },
      { topic: 'Cost optimization', task: 'Reduce API spend by 60% using caching and model routing' },
    ],
    assessment: 'Build a multi-model AI assistant that routes to optimal model per task type with cost tracking',
    keyConceptsForFlashcard: ['function calling', 'streaming tokens', 'context management', 'model routing'],
  },
  {
    weekNum: 55, title: 'Retrieval-Augmented Generation', phase: 4, phaseLabel: 'AI Integration',
    days: [
      { topic: 'RAG architecture', task: 'Build a basic RAG pipeline: ingest → chunk → embed → retrieve → generate' },
      { topic: 'Chunking strategies', task: 'Compare fixed-size, semantic, and recursive chunking on 3 doc types' },
      { topic: 'Hybrid search', task: 'Combine BM25 keyword search with vector similarity for better recall' },
      { topic: 'Reranking', task: 'Add Cohere reranker to improve relevance of retrieved chunks' },
      { topic: 'RAG evaluation', task: 'Measure faithfulness, relevance, and groundedness with RAGAS' },
    ],
    assessment: 'Build a document Q&A system with hybrid search, reranking, and RAGAS evaluation',
    keyConceptsForFlashcard: ['chunking overlap', 'hybrid search', 'reranking', 'RAGAS metrics'],
  },
  {
    weekNum: 56, title: 'Vector DBs & Embeddings', phase: 4, phaseLabel: 'AI Integration',
    days: [
      { topic: 'Embeddings deep dive', task: 'Compare text-embedding-3-small vs large on a retrieval benchmark' },
      { topic: 'Pinecone & Weaviate', task: 'Index 50K documents in Pinecone with metadata filtering' },
      { topic: 'pgvector', task: 'Add vector search to an existing PostgreSQL database with pgvector' },
      { topic: 'Similarity search tuning', task: 'Tune HNSW parameters for recall vs latency tradeoff' },
      { topic: 'Multi-modal embeddings', task: 'Build an image + text similarity search with CLIP embeddings' },
    ],
    assessment: 'Build a semantic search engine over 100K documents with sub-100ms p99 latency',
    keyConceptsForFlashcard: ['cosine similarity', 'HNSW', 'ANN vs exact search', 'embedding drift'],
  },
  {
    weekNum: 57, title: 'LangChain & AI Agents', phase: 4, phaseLabel: 'AI Integration',
    days: [
      { topic: 'LangChain fundamentals', task: 'Build a document analysis chain with memory and callbacks' },
      { topic: 'LangGraph agents', task: 'Build a ReAct agent that can search, code, and reason' },
      { topic: 'Tool use & planning', task: 'Create an agent with 5 tools: web search, code exec, DB query' },
      { topic: 'Multi-agent systems', task: 'Orchestrate a researcher + writer + editor multi-agent pipeline' },
      { topic: 'Agent memory', task: 'Add long-term memory to an agent using vector store + summary' },
    ],
    assessment: 'Build a research agent that autonomously gathers, analyzes, and reports on a topic',
    keyConceptsForFlashcard: ['ReAct', 'tool calling loop', 'agent scratchpad', 'LangGraph nodes'],
  },
  {
    weekNum: 58, title: 'AI in Production', phase: 4, phaseLabel: 'AI Integration',
    days: [
      { topic: 'LLM observability with LangSmith', task: 'Trace all LLM calls, identify slow and expensive chains' },
      { topic: 'Prompt versioning', task: 'Set up prompt management with A/B testing and rollback' },
      { topic: 'Rate limiting & fallbacks', task: 'Add provider fallback: OpenAI → Anthropic → local model' },
      { topic: 'Latency optimization', task: 'Reduce p99 LLM latency by 40% using streaming and caching' },
      { topic: 'AI feature flags', task: 'Gate AI features by user segment with gradual rollout' },
    ],
    assessment: 'Instrument an AI feature for production: tracing, cost dashboard, and fallback system',
    keyConceptsForFlashcard: ['LLM observability', 'prompt versioning', 'semantic caching', 'fallback chain'],
  },
  {
    weekNum: 59, title: 'Fine-tuning', phase: 4, phaseLabel: 'AI Integration',
    days: [
      { topic: 'When to fine-tune vs RAG', task: 'Write a decision framework for fine-tune vs RAG vs prompt eng' },
      { topic: 'OpenAI fine-tuning', task: 'Fine-tune GPT-4o-mini on 500 domain-specific examples' },
      { topic: 'LoRA & PEFT', task: 'Fine-tune Llama 3 with LoRA on a custom dataset' },
      { topic: 'Dataset curation', task: 'Build a data pipeline to clean, deduplicate, and format training data' },
      { topic: 'Evaluation & iteration', task: 'Evaluate fine-tuned model vs base model on held-out test set' },
    ],
    assessment: 'Fine-tune a model for a specific domain task, outperforming base model by 15%+',
    keyConceptsForFlashcard: ['LoRA rank', 'catastrophic forgetting', 'RLHF', 'DPO'],
  },
  {
    weekNum: 60, title: 'AI Safety & Evals', phase: 4, phaseLabel: 'AI Integration',
    days: [
      { topic: 'AI safety fundamentals', task: 'Document 10 failure modes for an AI coding assistant' },
      { topic: 'Eval frameworks', task: 'Build an eval suite with 100 test cases for a RAG system' },
      { topic: 'Guardrails & moderation', task: 'Add content moderation with Llama Guard and custom rules' },
      { topic: 'Hallucination detection', task: 'Build a faithfulness checker using NLI model' },
      { topic: 'Red teaming', task: 'Red team an AI system, document 5 jailbreaks, propose fixes' },
    ],
    assessment: 'Build a comprehensive eval suite for an AI system with safety, quality, and performance tests',
    keyConceptsForFlashcard: ['faithfulness', 'groundedness', 'NLI', 'constitutional AI'],
  },
  {
    weekNum: 61, title: 'Multi-modal AI', phase: 4, phaseLabel: 'AI Integration',
    days: [
      { topic: 'Vision models (GPT-4V)', task: 'Build an image analysis tool that describes and classifies images' },
      { topic: 'Image generation', task: 'Build a DALL-E 3 image generation tool with prompt refinement' },
      { topic: 'Speech-to-text & TTS', task: 'Build a voice interface with Whisper + TTS with streaming' },
      { topic: 'Document understanding', task: 'Extract structured data from PDFs and images with vision models' },
      { topic: 'Multi-modal RAG', task: 'Build a system that answers questions about images + text documents' },
    ],
    assessment: 'Build a multi-modal AI assistant handling text, images, voice, and document inputs',
    keyConceptsForFlashcard: ['vision encoding', 'Whisper architecture', 'multi-modal embedding', 'document AI'],
  },
  {
    weekNum: 62, title: 'AI Infrastructure & Cost', phase: 4, phaseLabel: 'AI Integration',
    days: [
      { topic: 'Self-hosted models with Ollama', task: 'Run Llama 3 locally, benchmark vs GPT-4o on tasks' },
      { topic: 'vLLM & inference optimization', task: 'Deploy vLLM with continuous batching for 10x throughput' },
      { topic: 'Cost modeling', task: 'Build a cost calculator for different model/provider combinations' },
      { topic: 'GPU infrastructure', task: 'Set up a GPU instance on Lambda Labs for model serving' },
      { topic: 'Model serving patterns', task: 'Implement model caching, warm-up, and auto-scaling' },
    ],
    assessment: 'Design and cost-model an AI infrastructure for 1M requests/day at <$500/month',
    keyConceptsForFlashcard: ['continuous batching', 'quantization', 'KV cache', 'speculative decoding'],
  },
  {
    weekNum: 63, title: 'AI Product Design', phase: 4, phaseLabel: 'AI Integration',
    days: [
      { topic: 'AI UX patterns', task: 'Audit 5 AI products, document 10 UX patterns for AI interfaces' },
      { topic: 'Streaming UI', task: 'Build a streaming chat UI with token-by-token rendering' },
      { topic: 'Human-in-the-loop', task: 'Add approval workflow for AI-generated content before publishing' },
      { topic: 'Progressive disclosure', task: 'Design AI confidence indicators and source citations UI' },
      { topic: 'Error states & fallbacks', task: 'Design graceful degradation for all AI failure modes' },
    ],
    assessment: 'Design and build an AI feature with streaming, citations, confidence, and error states',
    keyConceptsForFlashcard: ['streaming UI', 'HITL', 'AI confidence UX', 'graceful degradation'],
  },
  {
    weekNum: 64, title: 'Phase 4 Capstone', phase: 4, phaseLabel: 'AI Integration',
    days: [
      { topic: 'AI product planning', task: 'Design an AI-powered feature end-to-end: UX, API, eval plan' },
      { topic: 'RAG + agents implementation', task: 'Build the core AI pipeline with RAG and tool-using agent' },
      { topic: 'Production instrumentation', task: 'Add LangSmith tracing, cost tracking, and eval suite' },
      { topic: 'UI implementation', task: 'Build the streaming UI with citations and confidence indicators' },
      { topic: 'Deployment & monitoring', task: 'Deploy with fallbacks, rate limiting, and cost alerts' },
    ],
    assessment: 'Present Phase 4 capstone: AI system demo, eval results, cost analysis, and safety audit',
    keyConceptsForFlashcard: ['AI product lifecycle', 'eval-driven development', 'AI cost optimization', 'safety by design'],
  },

  // ── PHASE 5: DEVOPS ───────────────────────────────────────────────────────
  {
    weekNum: 65, title: 'Docker & Containerization', phase: 5, phaseLabel: 'DevOps',
    days: [
      { topic: 'Docker fundamentals', task: 'Containerize a Node.js API with multi-stage build' },
      { topic: 'Docker Compose', task: 'Orchestrate a 5-service app: API, DB, Redis, Worker, Nginx' },
      { topic: 'Image optimization', task: 'Reduce a 1.2GB image to under 150MB using distroless' },
      { topic: 'Networking & volumes', task: 'Configure custom networks and named volumes for persistence' },
      { topic: 'Docker security', task: 'Run containers as non-root, scan with Trivy, fix CVEs' },
    ],
    assessment: 'Containerize a full-stack app with Docker Compose, optimized images, and security hardening',
    keyConceptsForFlashcard: ['layer caching', 'distroless', 'bind mount vs volume', 'container networking'],
  },
  {
    weekNum: 66, title: 'Kubernetes Fundamentals', phase: 5, phaseLabel: 'DevOps',
    days: [
      { topic: 'K8s architecture', task: 'Diagram control plane, nodes, and how a pod is scheduled' },
      { topic: 'Pods, deployments, services', task: 'Deploy a 3-tier app with Deployment, Service, and Ingress' },
      { topic: 'ConfigMaps & Secrets', task: 'Externalize all config and secrets from application code' },
      { topic: 'Probes & resource limits', task: 'Add liveness, readiness, startup probes and resource quotas' },
      { topic: 'Helm charts', task: 'Package and deploy an app with a custom Helm chart' },
    ],
    assessment: 'Deploy a microservices app on K8s with Helm, Ingress, resource limits, and health probes',
    keyConceptsForFlashcard: ['etcd', 'kube-proxy', 'service types', 'Helm values'],
  },
  {
    weekNum: 67, title: 'Kubernetes Advanced', phase: 5, phaseLabel: 'DevOps',
    days: [
      { topic: 'HPA & VPA autoscaling', task: 'Configure HPA scaling from 2 to 20 pods based on CPU + custom metrics' },
      { topic: 'StatefulSets & storage', task: 'Deploy PostgreSQL as a StatefulSet with persistent volumes' },
      { topic: 'RBAC & network policies', task: 'Lock down cluster with RBAC roles and deny-all network policies' },
      { topic: 'Operators & CRDs', task: 'Use the Postgres operator to manage database lifecycle' },
      { topic: 'K8s observability', task: 'Deploy Prometheus + Grafana stack with K8s dashboards' },
    ],
    assessment: 'Run a production-grade K8s cluster with autoscaling, RBAC, network policies, and monitoring',
    keyConceptsForFlashcard: ['HPA algorithm', 'PVC binding', 'network policy', 'operator pattern'],
  },
  {
    weekNum: 68, title: 'CI/CD Pipelines', phase: 5, phaseLabel: 'DevOps',
    days: [
      { topic: 'GitHub Actions fundamentals', task: 'Build CI pipeline: lint, test, build, Docker push on PR' },
      { topic: 'CD with ArgoCD', task: 'Set up GitOps CD with ArgoCD deploying to K8s on merge' },
      { topic: 'Secrets in CI/CD', task: 'Manage secrets with GitHub Secrets and Vault integration' },
      { topic: 'Release strategies', task: 'Implement blue/green and canary deployments with ArgoCD Rollouts' },
      { topic: 'Pipeline optimization', task: 'Reduce CI time from 12 minutes to 3 minutes with caching' },
    ],
    assessment: 'Build end-to-end GitOps pipeline: PR triggers CI, merge triggers CD with canary rollout',
    keyConceptsForFlashcard: ['GitOps', 'ArgoCD sync', 'canary analysis', 'pipeline caching'],
  },
  {
    weekNum: 69, title: 'Terraform & IaC', phase: 5, phaseLabel: 'DevOps',
    days: [
      { topic: 'Terraform fundamentals', task: 'Provision a VPC, EC2, RDS, and S3 bucket with Terraform' },
      { topic: 'Modules & reusability', task: 'Extract 3 reusable Terraform modules for common patterns' },
      { topic: 'State management', task: 'Configure remote state with S3 backend and state locking' },
      { topic: 'Terraform Cloud & CD', task: 'Run Terraform plans in CI and apply on merge' },
      { topic: 'Drift detection', task: 'Detect and remediate infrastructure drift automatically' },
    ],
    assessment: 'Provision a complete AWS environment with Terraform: VPC, EKS, RDS, and CDN',
    keyConceptsForFlashcard: ['state lock', 'plan vs apply', 'terraform import', 'resource lifecycle'],
  },
  {
    weekNum: 70, title: 'Platform Engineering', phase: 5, phaseLabel: 'DevOps',
    days: [
      { topic: 'Internal developer platform', task: 'Build a service catalog with templates for new services' },
      { topic: 'Backstage', task: 'Set up Backstage with service catalog and TechDocs' },
      { topic: 'Golden paths', task: 'Create a golden path template for new Node.js microservices' },
      { topic: 'Cost management', task: 'Set up AWS Cost Explorer alerts and right-sizing recommendations' },
      { topic: 'Developer experience metrics', task: 'Measure DORA metrics: deployment frequency, MTTR, change failure rate' },
    ],
    assessment: 'Build a minimal IDP with service templates, catalog, and DORA metrics dashboard',
    keyConceptsForFlashcard: ['DORA metrics', 'golden path', 'IDP', 'cognitive load'],
  },
  {
    weekNum: 71, title: 'SecOps & Compliance', phase: 5, phaseLabel: 'DevOps',
    days: [
      { topic: 'Secrets management with Vault', task: 'Migrate all hardcoded secrets to HashiCorp Vault' },
      { topic: 'Container security scanning', task: 'Add Trivy to CI, block deploys with critical CVEs' },
      { topic: 'SAST & DAST', task: 'Run Semgrep SAST and OWASP ZAP DAST in CI pipeline' },
      { topic: 'Compliance as code', task: 'Implement OPA Gatekeeper policies for K8s compliance' },
      { topic: 'Incident response', task: 'Write runbooks for 5 common production incidents' },
    ],
    assessment: 'Implement a security pipeline: secrets, scanning, SAST/DAST, and compliance policies',
    keyConceptsForFlashcard: ['Vault dynamic secrets', 'OPA', 'SAST vs DAST', 'CVE scoring'],
  },
  {
    weekNum: 72, title: 'Phase 5 Capstone', phase: 5, phaseLabel: 'DevOps',
    days: [
      { topic: 'Infrastructure design', task: 'Design production AWS infrastructure for a SaaS application' },
      { topic: 'IaC implementation', task: 'Provision entire infrastructure with Terraform modules' },
      { topic: 'K8s deployment', task: 'Deploy app with Helm, autoscaling, and monitoring stack' },
      { topic: 'CI/CD pipeline', task: 'GitOps pipeline with canary deployments and rollback' },
      { topic: 'Security & documentation', task: 'Security scan, compliance check, and runbook documentation' },
    ],
    assessment: 'Present Phase 5 capstone: live infrastructure demo, deployment, and DORA metrics',
    keyConceptsForFlashcard: ['infrastructure as code', 'GitOps', 'zero-downtime deploy', 'cost optimization'],
  },

  // ── PHASE 6: CAPSTONE ─────────────────────────────────────────────────────
  {
    weekNum: 73, title: 'Capstone: Planning & Architecture', phase: 6, phaseLabel: 'Capstone',
    days: [
      { topic: 'Product definition', task: 'Write a full PRD for your capstone project' },
      { topic: 'System architecture', task: 'Design the complete system architecture with diagrams' },
      { topic: 'Tech stack decisions', task: 'Document all technology choices with justifications' },
      { topic: 'Data model design', task: 'Design the complete data model across all services' },
      { topic: 'Sprint planning', task: 'Create a 7-week sprint plan with milestones and deliverables' },
    ],
    assessment: 'Architecture review: PRD, system design, data model, and sprint plan approved by mentor',
    keyConceptsForFlashcard: ['PRD structure', 'ADR', 'C4 model', 'sprint planning'],
  },
  {
    weekNum: 74, title: 'Capstone: Sprint 1 — Backend', phase: 6, phaseLabel: 'Capstone',
    days: [
      { topic: 'Core API implementation', task: 'Build all backend API endpoints for the core domain' },
      { topic: 'Database & migrations', task: 'Implement full database schema with seed data' },
      { topic: 'Authentication', task: 'Add complete auth: JWT, OAuth, RBAC with tests' },
      { topic: 'Core business logic', task: 'Implement all core business logic with 80%+ test coverage' },
      { topic: 'Sprint 1 review', task: 'Demo backend APIs in Postman, document all endpoints' },
    ],
    assessment: 'Sprint 1 demo: all backend endpoints working, tested, and documented',
    keyConceptsForFlashcard: ['API-first development', 'contract testing', 'database seeding', 'test coverage'],
  },
  {
    weekNum: 75, title: 'Capstone: Sprint 2 — Frontend', phase: 6, phaseLabel: 'Capstone',
    days: [
      { topic: 'Core UI implementation', task: 'Build all main screens connected to real backend APIs' },
      { topic: 'State management', task: 'Implement full app state with TanStack Query and Zustand' },
      { topic: 'Authentication UI', task: 'Build login, signup, OAuth, and protected routes' },
      { topic: 'Responsive & accessible', task: 'Make all screens responsive and WCAG 2.1 AA compliant' },
      { topic: 'Sprint 2 review', task: 'Demo full frontend, run Lighthouse and accessibility audits' },
    ],
    assessment: 'Sprint 2 demo: all screens working, 90+ Lighthouse, WCAG compliant',
    keyConceptsForFlashcard: ['feature-based structure', 'route protection', 'optimistic UI', 'a11y audit'],
  },
  {
    weekNum: 76, title: 'Capstone: Sprint 3 — AI & Real-time', phase: 6, phaseLabel: 'Capstone',
    days: [
      { topic: 'AI feature implementation', task: 'Build the core AI feature: RAG, agents, or generative UI' },
      { topic: 'Real-time features', task: 'Add WebSocket-based real-time collaboration or notifications' },
      { topic: 'AI UI/UX', task: 'Build streaming UI, citations, confidence indicators' },
      { topic: 'Eval & safety', task: 'Add eval suite and guardrails for the AI feature' },
      { topic: 'Sprint 3 review', task: 'Demo AI features, show eval results and cost metrics' },
    ],
    assessment: 'Sprint 3 demo: AI feature working end-to-end with evals, cost tracking, and safety',
    keyConceptsForFlashcard: ['streaming UI', 'AI eval', 'real-time sync', 'cost per feature'],
  },
  {
    weekNum: 77, title: 'Capstone: Sprint 4 — DevOps', phase: 6, phaseLabel: 'Capstone',
    days: [
      { topic: 'Containerization', task: 'Dockerize all services with optimized multi-stage builds' },
      { topic: 'Infrastructure provisioning', task: 'Provision production infrastructure with Terraform' },
      { topic: 'K8s deployment', task: 'Deploy all services to K8s with Helm charts' },
      { topic: 'CI/CD pipeline', task: 'GitOps pipeline with automated tests and canary deploy' },
      { topic: 'Sprint 4 review', task: 'Demo production deployment with zero-downtime rollout' },
    ],
    assessment: 'Sprint 4 demo: production deployment live, CI/CD working, infrastructure documented',
    keyConceptsForFlashcard: ['multi-stage build', 'Helm values', 'canary deploy', 'IaC review'],
  },
  {
    weekNum: 78, title: 'Capstone: Hardening & Security', phase: 6, phaseLabel: 'Capstone',
    days: [
      { topic: 'Security audit', task: 'Run OWASP ZAP, Trivy, and Semgrep — fix all critical findings' },
      { topic: 'Performance optimization', task: 'Load test with k6, fix top 3 bottlenecks found' },
      { topic: 'Observability', task: 'Add full observability: logs, metrics, traces, and dashboards' },
      { topic: 'Error handling & resilience', task: 'Add circuit breakers, retries, and graceful degradation' },
      { topic: 'Disaster recovery', task: 'Test backup/restore, document RTO and RPO' },
    ],
    assessment: 'Security and reliability report: all critical issues resolved, SLOs defined and met',
    keyConceptsForFlashcard: ['threat modeling', 'SLO definition', 'chaos engineering', 'RTO vs RPO'],
  },
  {
    weekNum: 79, title: 'Capstone: Final Polish', phase: 6, phaseLabel: 'Capstone',
    days: [
      { topic: 'UI/UX polish', task: 'Fix all UI inconsistencies, add animations and micro-interactions' },
      { topic: 'Documentation', task: 'Write comprehensive README, API docs, and architecture guide' },
      { topic: 'Demo preparation', task: 'Record a 5-minute demo video showcasing all features' },
      { topic: 'Code review', task: 'Final code review: remove dead code, add missing comments' },
      { topic: 'Presentation prep', task: 'Prepare slides: problem, solution, architecture, demo, learnings' },
    ],
    assessment: 'Final demo video recorded, all documentation complete, code review passed',
    keyConceptsForFlashcard: ['storytelling with code', 'README structure', 'demo script', 'code review checklist'],
  },
  {
    weekNum: 80, title: 'Graduation', phase: 6, phaseLabel: 'Capstone',
    days: [
      { topic: 'Final presentation', task: 'Present capstone project to a live audience' },
      { topic: 'Portfolio update', task: 'Add capstone to portfolio with case study and live demo link' },
      { topic: 'Job search strategy', task: 'Update resume, LinkedIn, and GitHub with all 80-week projects' },
      { topic: 'Reflection & retrospective', task: 'Write a public blog post about your 80-week journey' },
      { topic: 'What\'s next', task: 'Plan your first 90 days as a Full-Stack AI Engineer' },
    ],
    assessment: 'Graduation: live capstone demo, portfolio reviewed, job application strategy finalized',
    keyConceptsForFlashcard: ['portfolio storytelling', 'technical interview prep', 'open source contribution', 'career roadmap'],
  },
];

export function getWeekData(weekNum: number): WeekData | undefined {
  return CURRICULUM.find(w => w.weekNum === weekNum);
}

export function getPhaseWeeks(phase: number): WeekData[] {
  return CURRICULUM.filter(w => w.phase === phase);
}

export const DAY_LABELS = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'];
export const DAY_SHORT = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri'];